import React from 'react';
import { useTranslation } from 'react-i18next';
import { ClipboardList, Bus, Plane, Users, CheckCircle2, ArrowRight } from 'lucide-react';
import { TripNoteEditor, type DriverNote } from './TripNoteEditor';
import { flightTextColumns } from '../all-trips/FlightCells';
import { locationChipStyle } from '@/lib/locationColors';

export interface PlanTrip {
  trip_number: string; from_location?: string; to_location?: string; departure_time?: string; total_passengers?: number;
  stops?: { location: string; time?: string }[]; flights?: { flight_number: string; arrival_time?: string }[]; flight_numbers?: string[];
  flight_times?: { time: string; airport?: string; flight_numbers?: string[] }[];
  vehicle?: string; driver_name?: string; companies?: string[]; cancelled?: boolean; manifest_url?: string | null;
  checked_in_count?: number; seat_count?: number; driver_note?: DriverNote | null; type_code?: string;
}
export interface PlanDay { date: string; day_name: string; formatted_date: string; trips: PlanTrip[]; published: boolean; is_history: boolean; visible: boolean }
export type SaveNote = (tripNumber: string, note: string) => Promise<boolean>;
export type LocColor = (loc: string | undefined | null) => string | null;
const noColor: LocColor = () => null;

/** Ett sted i ruten — fargekodet som i bookinglisten når stedet finnes i forkortelsene (samme farge, fullt navn beholdes). */
const RouteStop = ({ loc, color, time, testId }: { loc: string; color: string | null; time?: string; testId: string }) => (
  <span className={color ? 'px-1.5 py-0.5 rounded' : ''} style={locationChipStyle(color)} data-testid={testId} data-color={color || undefined}>
    {loc}{time ? <span className="text-slate-400 text-xs"> {time}</span> : null}
  </span>
);

/** «Passasjerliste»-knappen (/pl/-lenke) — brukes i mobilkortet og i «Liste»-kolonnen i tabellen. */
export const ManifestLink = ({ trip, compact = false }: { trip: PlanTrip; compact?: boolean }) => {
  const { t } = useTranslation();
  const canOpen = !!trip.manifest_url && !trip.cancelled;
  return (
    <a href={canOpen ? trip.manifest_url! : undefined} target="_blank" rel="noopener noreferrer" aria-disabled={!canOpen}
      className={`inline-flex items-center gap-1 rounded-md border font-medium whitespace-nowrap ${compact ? 'px-2 py-1 text-[11px]' : 'px-2.5 py-1.5 text-xs'} ${canOpen ? 'text-violet-700 border-violet-300 bg-violet-50 hover:bg-violet-100' : 'text-slate-400 border-slate-200 pointer-events-none'}`}
      title={canOpen ? '' : t('publicDriverPlan.listeIkkeTilgjengelig', 'Passasjerlisten er ikke tilgjengelig (utløpt eller ikke publisert)')}
      data-testid={`sp-manifest-${trip.trip_number}`}>
      <ClipboardList className="w-3.5 h-3.5" />{t('publicDriverPlan.passasjerliste', 'Passasjerliste')}
    </a>
  );
};

const TripRow = ({ trip, lang, onSaveNote, locColor = noColor }: { trip: PlanTrip; lang: string; onSaveNote?: SaveNote; locColor?: LocColor }) => {
  const { t } = useTranslation();
  const route = [trip.from_location, ...(trip.stops || []).map((s) => s.location), trip.to_location].filter(Boolean) as string[];
  const [flightNo, flightTime] = flightTextColumns(trip);  // iter 809: Flynr og Flytid hver for seg
  const stopCount = trip.stops?.length || 0;
  return (
    <div className={`flex flex-wrap items-start gap-x-4 gap-y-2 px-3 sm:px-4 py-3 ${trip.cancelled ? 'opacity-60' : ''}`} data-testid={`sp-trip-${trip.trip_number}`} data-cancelled={trip.cancelled ? 'true' : 'false'}>
      <div className="w-14 shrink-0">
        <div className="text-base font-bold text-slate-800 tabular-nums">{trip.departure_time || '—'}</div>
        <div className="text-[10px] font-mono text-slate-400 truncate" title={trip.trip_number}>{trip.trip_number}</div>
      </div>
      <div className="flex-1 min-w-[12rem]">
        <div className={`text-sm font-medium text-slate-800 flex flex-wrap items-center gap-x-1 gap-y-1 ${trip.cancelled ? 'line-through' : ''}`} data-testid={`sp-route-${trip.trip_number}`}>
          {route.map((loc, i) => (
            <React.Fragment key={`${loc}-${i}`}>
              {i > 0 && <ArrowRight className="w-3 h-3 text-slate-400" />}
              <RouteStop
                loc={loc}
                color={locColor(loc)}
                time={i > 0 && i <= stopCount ? trip.stops?.[i - 1]?.time : undefined}
                testId={`sp-route-${trip.trip_number}-${i === 0 ? 'from' : i <= stopCount ? `stop-${i}` : 'to'}`}
              />
            </React.Fragment>
          ))}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
          <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" />{trip.total_passengers ?? 0} pax</span>
          {trip.type_code && <span className="inline-flex items-center gap-1 font-bold text-slate-700" title={t('weeklyDateView.typeTitle', 'Type som i Booking-listen: T taxi · MB minibuss · B buss · S = delt · P per passasjer')} data-testid={`sp-type-${trip.trip_number}`}><span className="text-slate-400 font-normal">{t('weeklyDateView.type', 'Type')}:</span> {trip.type_code}</span>}
          {(trip.seat_count || 0) > 0 && <span className="inline-flex items-center gap-1 text-emerald-700" data-testid={`sp-counted-${trip.trip_number}`}><CheckCircle2 className="w-3 h-3" />{t('publicDriverPlan.opptalt', 'Opptalt')} {trip.checked_in_count || 0}/{trip.seat_count}</span>}
          {trip.vehicle && <span className="inline-flex items-center gap-1"><Bus className="w-3 h-3" />{trip.vehicle}</span>}
          {flightNo && <span className="inline-flex items-center gap-1" data-testid={`sp-flight-no-${trip.trip_number}`}><Plane className="w-3 h-3" /><span className="text-slate-400">{t('weeklyDateView.flynr', 'Flynr')}:</span> {flightNo}</span>}
          {flightTime && <span className="inline-flex items-center gap-1 font-mono" data-testid={`sp-flight-time-${trip.trip_number}`}><span className="text-slate-400 font-sans">{t('weeklyDateView.flytid', 'Flytid')}:</span> {flightTime}</span>}
          {trip.companies?.length ? <span className="truncate max-w-[16rem]" title={trip.companies.join(', ')}>{trip.companies.join(', ')}</span> : null}
          {trip.cancelled && <span className="text-red-600 font-semibold">{t('publicDriverPlan.innstilt', 'Innstilt')}</span>}
        </div>
      </div>
      <ManifestLink trip={trip} />
      {onSaveNote && <TripNoteEditor tripNumber={trip.trip_number} note={trip.driver_note} onSave={onSaveNote} lang={lang} />}
    </div>
  );
};

// Én dag i mottakerens kjøreplan: overskrift + turer (eller «Ingen turer denne dagen»).
export function DriverPlanDayCard({ day, lang, onSaveNote, locColor }: { day: PlanDay; lang: string; onSaveNote?: SaveNote; locColor?: LocColor }) {
  const { t } = useTranslation();
  const label = (() => { try { return new Date(`${day.date}T00:00:00`).toLocaleDateString(lang === 'en' ? 'en-GB' : 'nb-NO', { weekday: 'long', day: 'numeric', month: 'long' }); } catch { return `${day.day_name} ${day.formatted_date}`; } })();
  return (
    <section className="bg-white rounded-2xl shadow-sm overflow-hidden" data-testid={`sp-day-${day.date}`} data-state={day.is_history ? 'history' : 'published'}>
      <header className="flex flex-wrap items-center justify-between gap-2 px-3 sm:px-4 py-2 bg-slate-100 border-b">
        <h2 className="text-sm sm:text-base font-semibold text-slate-800 capitalize">{label}</h2>
        <span className="text-xs text-slate-500">{day.trips.length} {day.trips.length === 1 ? t('trips.tripLowercase', 'tur') : t('trips.tripsLowercase', 'turer')}</span>
      </header>
      {day.trips.length === 0 ? (
        <p className="px-4 py-3 text-sm italic text-slate-400" data-testid={`sp-day-empty-${day.date}`}>{t('trips.noTripsThisDay', 'Ingen turer denne dagen')}</p>
      ) : (
        <div className="divide-y">{day.trips.map((tr) => <TripRow key={tr.trip_number} trip={tr} lang={lang} onSaveNote={onSaveNote} locColor={locColor} />)}</div>
      )}
    </section>
  );
}
